import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';

// TS port of the source (Falah) app's pages/AnnouncementForm.jsx — ported
// as-is (generic module). Matches AnnouncementController::validated()
// exactly: `priority` is `nullable|in:normal,important,urgent`, `expires_at`
// must be `after:published_at` (published_at defaults to now() server-side
// when omitted on create).
interface AnnouncementFormState {
  title: string;
  body: string;
  priority: string;
  expires_at: string;
}

const emptyForm: AnnouncementFormState = { title: '', body: '', priority: 'normal', expires_at: '' };

interface AnnouncementRecord {
  title: string;
  body: string;
  priority: string;
  expires_at: string | null;
}

type FormErrors = Record<string, string[]>;

export function AnnouncementForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState<AnnouncementFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loaded, setLoaded] = useState(isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew || !id) return;
    api.get<AnnouncementRecord>(`/announcements/${id}`).then((res) => {
      const a = res.data;
      setForm({
        title: a.title,
        body: a.body,
        priority: a.priority,
        expires_at: a.expires_at ? a.expires_at.slice(0, 16) : '',
      });
      setLoaded(true);
    });
  }, [id, isNew]);

  const set =
    (key: keyof AnnouncementFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    const payload = { ...form, expires_at: form.expires_at || null };
    try {
      if (isNew) await api.post('/announcements', payload);
      else await api.put(`/announcements/${id}`, payload);
      navigate('/announcements');
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <>
      <header className="page-header">
        <h1>{isNew ? 'New announcement' : 'Edit announcement'}</h1>
      </header>

      <div className="card">
        <form onSubmit={save} className="form-grid">
          {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
          <label className="span-2">
            Title *
            <input value={form.title} onChange={set('title')} required />
            {errors.title && <small className="error">{errors.title[0]}</small>}
          </label>
          <label className="span-2">
            Body *
            <textarea rows={4} value={form.body} onChange={set('body')} required />
            {errors.body && <small className="error">{errors.body[0]}</small>}
          </label>
          <label>
            Priority
            <select value={form.priority} onChange={set('priority')}>
              <option value="normal">Normal</option>
              <option value="important">Important</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
          <label>
            Expires
            <input type="datetime-local" value={form.expires_at} onChange={set('expires_at')} />
            {errors.expires_at && <small className="error">{errors.expires_at[0]}</small>}
          </label>
          <div className="form-actions span-2">
            <Link className="btn btn-ghost" to="/announcements">
              Cancel
            </Link>
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
